import { useParams } from "react-router-dom";
import { useGetTelehealthSessionInfo } from "../../../hooks";
import { useCallback, useMemo } from "react";
import { EventTypeEnum, TrackParams } from "../../../segment/types";
import { useTrackEvent } from "../../../segment/segment";

/**
 * Small wrapper around useTrackEvent that adds properties for the current
 * patient, provider, and appointment (if available) to each event callback.
 */
export function useTrackIntroRoomEvent() {
  const { providerShortId, patientShortId } = useParams();
  const { data, loading } = useGetTelehealthSessionInfo(
    providerShortId,
    patientShortId,
  );
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const {
    formSubmitted: formSubmittedBase,
    userViewed: userViewedBase,
    userClicked: userClickedBase,
  } = useTrackEvent();

  const trackEvent = useCallback(
    (
      baseTrackEventCallback:
        | typeof formSubmittedBase
        | typeof userViewedBase
        | typeof userClickedBase,
    ) => {
      return (event: EventTypeEnum, properties?: Partial<TrackParams>) => {
        if (loading || !patientShortId || !providerShortId) {
          return;
        }
        baseTrackEventCallback(event, {
          patientShortId,
          providerShortId,
          appointmentShortId,
          ...properties,
        });
      };
    },
    [appointmentShortId, loading, patientShortId, providerShortId],
  );

  return useMemo(
    () => ({
      formSubmitted: trackEvent(formSubmittedBase),
      userViewed: trackEvent(userViewedBase),
      userClicked: trackEvent(userClickedBase),
    }),
    [trackEvent, formSubmittedBase, userViewedBase, userClickedBase],
  );
}
